import { useEffect } from 'react';

/**
 * React hook that invokes a callback when a mouse click occurs
 * outside of the provided element reference.
 *
 * @param ref - A React ref to the element that should ignore outside clicks.
 * @param callback - Function to run when a click happens outside `ref`.
 *
 * @remarks
 * - Adds a `mousedown` listener on mount and removes it on unmount.
 * - Re-subscribes when `ref` or `callback` change.
 *
 * @example
 * const panelRef = useRef<HTMLDivElement>(null);
 * useClickOutside(panelRef, () => setOpen(false));
 */
export function useClickOutside(ref: React.RefObject<HTMLElement> | null, callback: () => void) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!ref?.current) {
        return;
      }
      if (!ref.current.contains(event.target as Node)) {
        callback();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, callback]);
}
